/**
 * Unified Performance Monitoring Service
 * Consolidated monitoring system for performance, API calls, and user interactions
 */

class PerformanceMonitor {
  constructor() {
    this.metrics = {
      pageLoad: {},
      apiCalls: [],
      userInteractions: [],
      performance: [],
      errors: [],
      cacheHits: 0,
      cacheMisses: 0
    };
    
    this.thresholds = {
      pageLoad: 30000, // 30 seconds (very lenient for development)
      apiCall: 10000,  // 10 seconds (very lenient for development)
      interaction: 2000 // 2 seconds (very lenient for development)
    };
    
    this.observers = new Map();
    this.isEnabled = false; // Disabled to prevent navigation throttling
    this.startTime = Date.now();
    this.isMonitoring = false;
    this.lastIssueTime = 0; // Throttle issue reporting
    this.lastNavigationTime = 0;
    this.lastResourceTime = 0;
    this.lastLongTaskTime = 0;
    
    // DISABLED: Performance monitoring can cause navigation throttling
    // Only enable in production and when explicitly requested
    if (false && import.meta.env.PROD && import.meta.env.VITE_ENABLE_PERFORMANCE_MONITORING === 'true') {
      this.isEnabled = true;
      this.initializeObservers();
      this.startMonitoring();
    }
  }

  // No-op methods for development
  startMonitoring() { return; }
  stopMonitoring() { return; }
  trackApiCall() { return; }
  trackUserInteraction() { return; }
  trackError() { return; }
  trackCacheHit() { return; }
  trackCacheMiss() { return; }
  trackSearch() { return; }
  trackCache() { return; }
  trackPerformanceMetric() { return; }
  getMetrics() { return this.metrics; }
  getPerformanceSummary() { return this.metrics; }
  getComprehensiveMetrics() { return this.metrics; }
  generateSampleData() { return; }
  reportPerformanceIssue() { return; }
  sendToMonitoring() { return; }
  measureCoreWebVitals() { return; }
  trackNavigationTiming() { return; }
  trackResourceTiming() { return; }
  trackLongTask() { return; }
  trackLCP() { return; }
  trackFID() { return; }
  trackCLS() { return; }
  cleanup() { return; }

  /**
   * Initialize performance observers
   */
  initializeObservers() {
    // DISABLED: Navigation timing observer to prevent navigation throttling
    // The navigation observer can interfere with React Router navigation
    // and cause the "Throttling navigation to prevent the browser from hanging" error
    if (false && 'PerformanceObserver' in window) {
      try {
        const navObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          // Throttle navigation timing to prevent excessive events
          const now = Date.now();
          if (this.lastNavigationTime && now - this.lastNavigationTime < 10000) {
            return; // Skip if less than 10 seconds since last navigation
          }
          this.lastNavigationTime = now;
          
          entries.forEach(entry => this.trackNavigationTiming(entry));
        });
        navObserver.observe({ entryTypes: ['navigation'] });
        this.observers.set('navigation', navObserver);
      } catch (error) {
        console.warn('Navigation timing observer failed:', error);
      }

      // Resource timing
      try {
        const resourceObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          // Throttle resource timing to prevent excessive events
          const now = Date.now();
          if (this.lastResourceTime && now - this.lastResourceTime < 5000) {
            return; // Skip if less than 5 seconds since last resource timing
          }
          this.lastResourceTime = now;
          
          entries.forEach(entry => this.trackResourceTiming(entry));
        });
        resourceObserver.observe({ entryTypes: ['resource'] });
        this.observers.set('resource', resourceObserver);
      } catch (error) {
        console.warn('Resource timing observer failed:', error);
      }

      // Long task detection
      try {
        const longTaskObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          // Throttle long task detection to prevent excessive events
          const now = Date.now();
          if (this.lastLongTaskTime && now - this.lastLongTaskTime < 10000) {
            return; // Skip if less than 10 seconds since last long task
          }
          this.lastLongTaskTime = now;
          
          entries.forEach(entry => this.trackLongTask(entry));
        });
        longTaskObserver.observe({ entryTypes: ['longtask'] });
        this.observers.set('longtask', longTaskObserver);
      } catch (error) {
        console.warn('Long task observer failed:', error);
      }
    }

    // Core Web Vitals
    this.measureCoreWebVitals();
  }

  /**
   * Track navigation timing
   */
  trackNavigationTiming(entry) {
    // Skip tracking if performance monitoring is disabled
    if (!this.isEnabled) {
      return;
    }
    
    const timing = {
      domContentLoaded: entry.domContentLoadedEventEnd - entry.domContentLoadedEventStart,
      loadComplete: entry.loadEventEnd - entry.loadEventStart,
      totalTime: entry.loadEventEnd - entry.navigationStart,
      dns: entry.domainLookupEnd - entry.domainLookupStart,
      tcp: entry.connectEnd - entry.connectStart,
      request: entry.responseEnd - entry.requestStart,
      response: entry.responseEnd - entry.responseStart
    };

    this.metrics.pageLoad = timing;
    
    // Check if performance is within thresholds
    if (timing.totalTime > this.thresholds.pageLoad) {
      this.reportPerformanceIssue('slow_page_load', timing);
    }
  }

  /**
   * Track resource timing
   */
  trackResourceTiming(entry) {
    // Skip tracking if performance monitoring is disabled
    if (!this.isEnabled) {
      return;
    }
    
    const duration = entry.responseEnd - entry.startTime;
    
    if (duration > this.thresholds.apiCall) {
      this.reportPerformanceIssue('slow_resource', {
        name: entry.name,
        duration,
        size: entry.transferSize,
        type: entry.initiatorType
      });
    }
  }

  /**
   * Track long tasks
   */
  trackLongTask(entry) {
    // Skip tracking if performance monitoring is disabled
    if (!this.isEnabled) {
      return;
    }
    
    // Only report very long tasks (>500ms) to reduce noise
    if (entry.duration > 500) {
      this.reportPerformanceIssue('long_task', {
        duration: entry.duration,
        startTime: entry.startTime
      });
    }
  }

  /**
   * Measure Core Web Vitals
   */
  measureCoreWebVitals() {
    // Largest Contentful Paint (LCP)
    if ('PerformanceObserver' in window) {
      try {
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          this.metrics.lcp = lastEntry.startTime;
          
          // More lenient LCP threshold for development
          const lcpThreshold = import.meta.env.DEV ? 15000 : 4000; // 15s dev, 4s prod
          if (lastEntry.startTime > lcpThreshold) {
            this.reportPerformanceIssue('poor_lcp', { 
              value: lastEntry.startTime,
              threshold: lcpThreshold,
              element: lastEntry.element?.tagName || 'unknown'
            });
          }
        });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
        this.observers.set('lcp', lcpObserver);
      } catch (error) {
        console.warn('LCP observer failed:', error);
      }

      // First Input Delay (FID)
      try {
        const fidObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach(entry => {
            this.metrics.fid = entry.processingStart - entry.startTime;
            
            if (this.metrics.fid > 100) {
              this.reportPerformanceIssue('poor_fid', { value: this.metrics.fid });
            }
          });
        });
        fidObserver.observe({ entryTypes: ['first-input'] });
        this.observers.set('fid', fidObserver);
      } catch (error) {
        console.warn('FID observer failed:', error);
      }

      // Cumulative Layout Shift (CLS)
      try {
        let clsValue = 0;
        const clsObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach(entry => {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
            }
          });
          this.metrics.cls = clsValue;
          
          if (clsValue > 0.1) {
            this.reportPerformanceIssue('poor_cls', { value: clsValue });
          }
        });
        clsObserver.observe({ entryTypes: ['layout-shift'] });
        this.observers.set('cls', clsObserver);
      } catch (error) {
        console.warn('CLS observer failed:', error);
      }
    }
  }

  /**
   * Track API call performance
   */
  trackApiCall(url, startTime, endTime, success = true) {
    const duration = endTime - startTime;
    const key = this.sanitizeUrl(url);
    
    if (!this.metrics.apiCalls[key]) {
      this.metrics.apiCalls[key] = {
        count: 0,
        totalDuration: 0,
        avgDuration: 0,
        successCount: 0,
        errorCount: 0
      };
    }
    
    const apiMetric = this.metrics.apiCalls[key];
    apiMetric.count++;
    apiMetric.totalDuration += duration;
    apiMetric.avgDuration = apiMetric.totalDuration / apiMetric.count;
    
    if (success) {
      apiMetric.successCount++;
    } else {
      apiMetric.errorCount++;
    }
    
    if (duration > this.thresholds.apiCall) {
      this.reportPerformanceIssue('slow_api_call', {
        url: key,
        duration,
        success
      });
    }
  }

  /**
   * Track user interaction performance
   */
  trackUserInteraction(action, startTime, endTime) {
    // Skip tracking if performance monitoring is disabled
    if (!this.isEnabled) {
      return;
    }
    
    // Throttle tracking to prevent excessive events
    const now = Date.now();
    if (this.lastInteractionTime && now - this.lastInteractionTime < 1000) {
      return; // Skip if less than 1 second since last interaction
    }
    this.lastInteractionTime = now;
    
    const duration = endTime - startTime;
    
    if (!this.metrics.userInteractions[action]) {
      this.metrics.userInteractions[action] = {
        count: 0,
        totalDuration: 0,
        avgDuration: 0
      };
    }
    
    const interactionMetric = this.metrics.userInteractions[action];
    interactionMetric.count++;
    interactionMetric.totalDuration += duration;
    interactionMetric.avgDuration = interactionMetric.totalDuration / interactionMetric.count;
    
    if (duration > this.thresholds.interaction) {
      this.reportPerformanceIssue('slow_interaction', {
        action,
        duration
      });
    }
  }

  /**
   * Report performance issues
   */
  reportPerformanceIssue(type, data) {
    // Throttle issue reporting to prevent spam
    const now = Date.now();
    if (now - this.lastIssueTime < 5000) { // Only report once every 5 seconds
      return;
    }
    this.lastIssueTime = now;
    
    const issue = {
      type,
      data,
      timestamp: now,
      userAgent: navigator.userAgent,
      url: window.location.href
    };
    
    this.metrics.errors.push(issue);
    
    // Send to monitoring service in production
    if (import.meta.env.PROD && import.meta.env.VITE_METRICS_ENDPOINT) {
      this.sendToMonitoring(issue);
    }
    
    // Only log in development if performance monitoring is explicitly enabled
    if (import.meta.env.DEV && import.meta.env.VITE_DEBUG_MODE === 'true') {
      console.warn('Performance issue detected:', {
        ...issue,
        userAgent: navigator.userAgent,
        url: window.location.href,
        timestamp: new Date().toISOString()
      });
    }
  }

  /**
   * Send metrics to monitoring service
   */
  async sendToMonitoring(data) {
    try {
      await fetch(import.meta.env.VITE_METRICS_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });
    } catch (error) {
      console.warn('Failed to send metrics:', error);
    }
  }

  /**
   * Get performance summary
   */
  getPerformanceSummary() {
    return {
      pageLoad: this.metrics.pageLoad,
      coreWebVitals: {
        lcp: this.metrics.lcp,
        fid: this.metrics.fid,
        cls: this.metrics.cls
      },
      apiCalls: this.metrics.apiCalls,
      userInteractions: this.metrics.userInteractions,
      issues: this.metrics.errors.length,
      timestamp: Date.now()
    };
  }

  /**
   * Get all metrics data
   */
  getMetrics() {
    return {
      pageLoad: this.metrics.pageLoad,
      apiCalls: this.metrics.apiCalls,
      userInteractions: this.metrics.userInteractions,
      errors: this.metrics.errors,
      coreWebVitals: {
        lcp: this.metrics.lcp,
        fid: this.metrics.fid,
        cls: this.metrics.cls
      }
    };
  }

  /**
   * Sanitize URL for tracking
   */
  sanitizeUrl(url) {
    try {
      const urlObj = new URL(url);
      return `${urlObj.origin}${urlObj.pathname}`;
    } catch {
      return url;
    }
  }

  /**
   * Start monitoring
   */
  startMonitoring() {
    this.isMonitoring = true;
    // Silent monitoring start
  }

  /**
   * Stop monitoring
   */
  stopMonitoring() {
    this.isMonitoring = false;
    // Silent monitoring stop
  }

  /**
   * Track API call (consolidated from MonitoringService)
   */
  trackApiCall(apiName, duration, success = true) {
    if (!this.isMonitoring) return;

    const apiCall = {
      apiName,
      duration,
      success,
      timestamp: Date.now(),
      url: apiName
    };

    this.metrics.apiCalls.push(apiCall);
    
    // Keep only last 100 API calls
    if (this.metrics.apiCalls.length > 100) {
      this.metrics.apiCalls.shift();
    }

    // Track performance metrics
    if (success) {
      this.trackPerformanceMetric('api_call', duration);
    } else {
      this.trackError('api_error', `API call failed: ${apiName}`, { apiName, duration });
    }
  }

  /**
   * Track user interaction (consolidated from MonitoringService)
   */
  trackUserInteraction(action, duration, element = null) {
    if (!this.isMonitoring) return;

    // Throttle tracking to prevent excessive events
    const now = Date.now();
    if (this.lastInteractionTime && now - this.lastInteractionTime < 1000) {
      return; // Skip if less than 1 second since last interaction
    }
    this.lastInteractionTime = now;

    const interaction = {
      action,
      duration,
      element,
      timestamp: now
    };

    this.metrics.userInteractions.push(interaction);
    
    // Keep only last 50 interactions
    if (this.metrics.userInteractions.length > 50) {
      this.metrics.userInteractions.shift();
    }

    // Track performance metrics
    this.trackPerformanceMetric('user_interaction', duration);
  }

  /**
   * Track performance metric
   */
  trackPerformanceMetric(type, value) {
    if (!this.isMonitoring) return;

    const metric = {
      type,
      value,
      timestamp: Date.now()
    };

    this.metrics.performance.push(metric);
    
    // Keep only last 200 performance metrics
    if (this.metrics.performance.length > 200) {
      this.metrics.performance.shift();
    }
  }

  /**
   * Track error (consolidated from MonitoringService)
   */
  trackError(type, message, details = {}) {
    if (!this.isMonitoring) return;

    const error = {
      type,
      message,
      details,
      timestamp: Date.now()
    };

    this.metrics.errors.push(error);
    
    // Keep only last 50 errors
    if (this.metrics.errors.length > 50) {
      this.metrics.errors.shift();
    }
  }

  /**
   * Track cache hit/miss
   */
  trackCacheHit() {
    this.metrics.cacheHits++;
  }

  trackCacheMiss() {
    this.metrics.cacheMisses++;
  }

  /**
   * Track cache operation (hit or miss)
   * @param {boolean} isHit - Whether it's a cache hit
   * @param {string} context - Context of the cache operation
   */
  trackCache(isHit, context) {
    if (isHit) {
      return this.trackCacheHit();
    } else {
      return this.trackCacheMiss();
    }
  }

  /**
   * Track search operation
   * @param {string} query - Search query
   * @param {number} duration - Search duration
   * @param {Object} metadata - Search metadata
   */
  trackSearch(query, duration, metadata = {}) {
    return this.trackApiCall('search', duration, true);
  }

  /**
   * Get comprehensive metrics (consolidated from MonitoringService)
   */
  getComprehensiveMetrics() {
    const uptime = Date.now() - this.startTime;
    const apiCalls = this.metrics.apiCalls;
    const userInteractions = this.metrics.userInteractions;
    const performance = this.metrics.performance;
    const errors = this.metrics.errors;

    return {
      uptime,
      apiCalls: {
        total: apiCalls.length,
        success: apiCalls.filter(call => call.success).length,
        failed: apiCalls.filter(call => !call.success).length,
        averageDuration: apiCalls.length > 0 ? 
          apiCalls.reduce((sum, call) => sum + call.duration, 0) / apiCalls.length : 0
      },
      userInteractions: {
        total: userInteractions.length,
        averageDuration: userInteractions.length > 0 ? 
          userInteractions.reduce((sum, interaction) => sum + interaction.duration, 0) / userInteractions.length : 0
      },
      performance: {
        total: performance.length,
        averageValue: performance.length > 0 ? 
          performance.reduce((sum, metric) => sum + metric.value, 0) / performance.length : 0
      },
      errors: {
        total: errors.length,
        byType: errors.reduce((acc, error) => {
          acc[error.type] = (acc[error.type] || 0) + 1;
          return acc;
        }, {})
      },
      cache: {
        hits: this.metrics.cacheHits,
        misses: this.metrics.cacheMisses,
        hitRate: this.metrics.cacheHits + this.metrics.cacheMisses > 0 ? 
          this.metrics.cacheHits / (this.metrics.cacheHits + this.metrics.cacheMisses) : 0
      }
    };
  }

  /**
   * Generate sample data for dashboard demonstration
   */
  generateSampleData() {
    // Generate realistic sample data
    this.metrics.pageLoad = {
      loadTime: Math.random() * 2000 + 800, // 800-2800ms
      domContentLoaded: Math.random() * 1500 + 600, // 600-2100ms
      firstContentfulPaint: Math.random() * 1000 + 400, // 400-1400ms
      lcp: Math.random() * 2000 + 1000, // 1000-3000ms
      fid: Math.random() * 100 + 50, // 50-150ms
      cls: Math.random() * 0.1 + 0.05 // 0.05-0.15
    };

    // Generate API call metrics
    const apiCalls = ['address_search', 'geocoding', 'routing', 'reviews'];
    apiCalls.forEach(api => {
      this.metrics.apiCalls[api] = {
        count: Math.floor(Math.random() * 50) + 10,
        avgResponseTime: Math.random() * 1000 + 200,
        successRate: Math.random() * 0.2 + 0.8, // 80-100%
        lastCall: Date.now() - Math.random() * 3600000 // Within last hour
      };
    });

    // Generate user interaction metrics
    const interactions = ['click', 'scroll', 'form_submit', 'navigation'];
    interactions.forEach(interaction => {
      this.metrics.userInteractions[interaction] = {
        count: Math.floor(Math.random() * 100) + 20,
        avgResponseTime: Math.random() * 200 + 50,
        lastInteraction: Date.now() - Math.random() * 1800000 // Within last 30 min
      };
    });

    // Generate some sample errors
    this.metrics.errors = [
      {
        type: 'api_error',
        message: 'Temporary API timeout',
        timestamp: Date.now() - Math.random() * 3600000,
        severity: 'warning'
      }
    ];
  }

  /**
   * Cleanup observers
   */
  cleanup() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers.clear();
  }
}

// Create singleton instance (only if enabled)
let performanceMonitor = null;

const getPerformanceMonitor = () => {
  if (!performanceMonitor) {
    performanceMonitor = new PerformanceMonitor();
  }
  return performanceMonitor;
};

export default getPerformanceMonitor();
